import React, { useState } from 'react';
import type { TextStyle, ViewProps, ViewStyle } from 'react-native';
import { Pressable, StyleSheet, View } from 'react-native';
import { COLORS, SPACING } from '@garnish/constants';
import { BodyText } from '../Text';
import { List, ListItem } from '../List';
import { WithTooltip } from '../WithTooltip';
import { Modal, ModalDialogue } from '../Modal';
import { Button } from '../Button';
import { useBreakpointHelpers } from '../../hooks';

// ────────────────────────────────────────────────────────────────────────────────

export const Ledger = (props: LedgerProps) => {
  const {
    items,
    hasTopBorder,
    style,
    helperTextModalDismissLabel,
    helperTextToggleA11yLabel,
    ...restProps
  } = props;
  const { currentBreakpoint } = useBreakpointHelpers();

  return (
    <List
      style={[styles.ledger, hasTopBorder && styles.ledgerWithTopBorder, style]}
      {...restProps}
    >
      {items.map(
        (item, index) =>
          item && (
            // eslint-disable-next-line react/no-array-index-key
            <ListItem key={index}>
              <LedgerRow
                {...item}
                helperTextModalDismissLabel={helperTextModalDismissLabel}
                helperTextToggleA11yLabel={helperTextToggleA11yLabel}
                shouldUseHelperTextModal={currentBreakpoint.isMobile}
              />
            </ListItem>
          ),
      )}
    </List>
  );
};

// ────────────────────────────────────────────────────────────────────────────────

export const LedgerRow = (props: LedgerRowProps) => {
  const { label, amount, helperText, testID, ...restProps } = props;

  return (
    <View style={styles.ledgerItem}>
      {/* label */}
      <LedgerRowLabel label={label} helperText={helperText} {...restProps} />

      {/* amount */}
      <LedgerRowLabel
        label={`${Array.isArray(amount) ? amount.join(' + ') : amount}`}
        alignment="right"
        testID={testID}
        style={styles.ledgerRowAmountItem}
        {...restProps}
      />
    </View>
  );
};

export const LedgerRowLabel = (props: LedgerItemLabelProps) => {
  const {
    helperText,
    palette,
    bold,
    label,
    alignment = 'left',
    style,
    shouldUseHelperTextModal,
    helperTextModalDismissLabel = 'Got it',
    helperTextToggleA11yLabel = 'Toggle helper text',
    ...rest
  } = props;

  // ──────────────────────────────────────────────────────────────────────────────

  const textProps = useBodyTextProps({ palette, bold });
  const shouldUseUnderline = typeof helperText === 'string';

  const [shouldShowHelperModal, setShouldShowHelperModal] = useState(false);

  // ──────────────────────────────────────────────────────────────────────────────

  const toggleHelperTextModal = () => {
    setShouldShowHelperModal((currentValue) => !currentValue);
  };

  // ──────────────────────────────────────────────────────────────────────────────

  const labelEL = (
    <View style={[styles.ledgerRowItem, style]}>
      <BodyText
        {...textProps}
        {...rest}
        numberOfLines={1}
        underline={shouldUseUnderline}
        style={[
          textProps.style,
          alignment === 'right' && styles.ledgerItemRightAlignment,
        ]}
      >
        {label}
      </BodyText>
    </View>
  );

  // ──────────────────────────────────────────────────────────────────────────────

  if (helperText) {
    // <Modal> should be used in case of mobile breakpoint
    if (shouldUseHelperTextModal) {
      return (
        <>
          <Pressable
            onPress={toggleHelperTextModal}
            accessibilityRole="button"
            accessibilityLabel={helperTextToggleA11yLabel}
            style={styles.helpTextToggle}
          >
            {labelEL}
          </Pressable>

          <ModalDialogue
            visible={shouldShowHelperModal}
            onRequestClose={toggleHelperTextModal}
            headline={label}
            body={helperText}
          >
            <Modal.Footer withoutTopBorder withoutBottomPadding>
              <Button
                onPress={toggleHelperTextModal}
                palette="primary"
                accessibilityRole="button"
                accessibilityLabel={helperTextToggleA11yLabel}
                size="large"
              >
                {helperTextModalDismissLabel}
              </Button>
            </Modal.Footer>
          </ModalDialogue>
        </>
      );
    }

    // and <Tooltip> otherwise
    return (
      <WithTooltip
        text={helperText}
        placement="right"
        triggerMode="hover"
        toggleAccessibilityLabel={helperTextToggleA11yLabel}
      >
        {labelEL}
      </WithTooltip>
    );
  }

  // ──────────────────────────────────────────────────────────────────────────────

  return labelEL;
};

//
// ─── TYPES ──────────────────────────────────────────────────────────────────────
//

type LedgerProps = {
  readonly items: readonly LedgerItem[];
  readonly hasTopBorder?: boolean;
  readonly helperTextModalDismissLabel?: string;
  readonly helperTextToggleA11yLabel?: string;
} & Omit<ViewProps, 'children'>;

type LedgerRowProps = {
  readonly shouldUseHelperTextModal?: boolean;
} & LedgerItem &
  Pick<
    LedgerProps,
    'helperTextModalDismissLabel' | 'helperTextToggleA11yLabel'
  >;

type LedgerItemLabelProps = {
  readonly alignment?: 'right' | 'left';
  readonly style?: ViewStyle;
} & Omit<LedgerRowProps, 'amount'>;

type UseBodyTextProps = Pick<LedgerItem, 'bold' | 'palette'>;

type LedgerItem = {
  readonly label: string;

  /**
   * Amount can be represented as a simple string
   * or a collection of strings joined by " + " sign.
   */
  readonly amount: string | readonly string[];
  readonly bold?: boolean;
  readonly palette?: 'standard' | 'success';

  /**
   * Optional helper text to be shown in tooltip/modal.
   */
  readonly helperText?: string;
  readonly testID?: string;
};

//
// ─── STYLES ──────────────────────────────────────────────────────────────────────
//

const styles = StyleSheet.create({
  ledger: {
    paddingTop: SPACING.large,
    paddingBottom: SPACING.medium,
  },
  ledgerWithTopBorder: {
    borderTopWidth: 1,
    borderTopColor: COLORS.TEXT_COLOR,
  },
  ledgerItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: SPACING.small,
  },
  ledgerItemRightAlignment: {
    textAlign: 'right',
  },
  ledgerRowItem: {
    flexShrink: 1,
  },
  ledgerRowAmountItem: {
    flexShrink: 0,
  },
  helpTextToggle: {
    flexShrink: 1,
  },
});

const PALETTES_MAP = {
  success: COLORS.SUCCESS,
  standard: COLORS.TEXT_COLOR,
};

//
// ─── HELPERS ────────────────────────────────────────────────────────────────────
//

const useBodyTextProps = (
  props: UseBodyTextProps,
): Partial<React.ComponentProps<typeof BodyText>> => {
  const { palette = 'standard', bold } = props;
  const style = getTextStylesFromPresets(palette);
  const size = 4;

  return { size, style, bold };
};

const getTextStylesFromPresets = (
  palette: LedgerItem['palette'] = 'standard',
): TextStyle => {
  return { color: PALETTES_MAP[palette] };
};
